import { Trans, useLingui } from '@lingui/react/macro';
import { Check, FilePen, CalendarPlus, Loader2, Pencil, Trash2, User, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useCancelCollisionDetection, useDeleteProject, useUpdateProject } from '@/api/hooks';
import { ConfirmModal } from '@/components/ConfirmModal';
import { SimBannerContent } from '@/components/SimBanner';
import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import type { ProjectDetail } from '@/types/api';
import { apiErrorMessage } from '@/utils/apiError';
import { stateBannerClass } from '@/utils/simulationState';
import { absoluteTime, relativeTime } from '@/utils/time';

export const ProjectHeader = ({
  project,
  isLocked,
  onDeleted,
  simulationState,
  collisionCount,
  backgroundTaskId,
}: {
  project: ProjectDetail;
  isLocked: boolean;
  onDeleted: () => void;
  simulationState: ProjectDetail['simulation_state'];
  collisionCount?: number | null;
  backgroundTaskId?: string | null;
}): React.ReactElement => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const { t, i18n } = useLingui();

  const updateProjectMutation = useUpdateProject(project.id);
  const deleteProjectMutation = useDeleteProject();
  const cancelDetectionMutation = useCancelCollisionDetection(project.id);

  const handleStartEditName = (): void => {
    setEditNameValue(project.name);
    setIsEditingName(true);
  };

  const handleSaveName = (): void => {
    if (!editNameValue.trim()) return;
    updateProjectMutation.mutate(
      { name: editNameValue.trim() },
      {
        onSuccess: () => {
          setIsEditingName(false);
          toast.success(t`Project name saved.`);
        },
        onError: (e) => toast.error(apiErrorMessage(e, t`Failed to save project name.`)),
      }
    );
  };

  const handleCancelEditName = (): void => {
    setEditNameValue('');
    setIsEditingName(false);
  };

  const handleDelete = (): void => {
    deleteProjectMutation.mutate(project.id, {
      onSuccess: () => {
        toast.success(t`Project deleted.`);
        onDeleted();
      },
      onError: (e) => toast.error(apiErrorMessage(e, t`Failed to delete project.`)),
    });
  };

  const handleConfirmCancel = (): void => {
    cancelDetectionMutation.mutate(undefined, {
      onSuccess: () => setShowCancelConfirm(false),
      onError: (e) => toast.error(apiErrorMessage(e, t`Failed to cancel collision detection.`)),
    });
  };

  return (
    <>
      <Card>
        <CardHeader>
          {isEditingName ? (
            <>
              <CardTitle>
                <input
                  type="text"
                  value={editNameValue}
                  onChange={(e) => setEditNameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveName();
                    if (e.key === 'Escape') handleCancelEditName();
                  }}
                  disabled={updateProjectMutation.isPending}
                  ref={(el) => {
                    if (el) el.focus();
                  }}
                  className="w-full rounded-md border border-slate-300 -ml-2 -my-1 px-2 py-1 text-lg font-bold text-slate-800"
                />
              </CardTitle>
              <CardAction>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    onClick={handleSaveName}
                    disabled={updateProjectMutation.isPending || !editNameValue.trim()}
                  >
                    {updateProjectMutation.isPending ? <Loader2 className="animate-spin" /> : <Check />}{' '}
                    <span className="hidden sm:inline">
                      <Trans>Save</Trans>
                    </span>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancelEditName}
                    disabled={updateProjectMutation.isPending}
                  >
                    <X />
                    <span className="hidden sm:inline">
                      <Trans>Cancel</Trans>
                    </span>
                  </Button>
                </div>
              </CardAction>
            </>
          ) : (
            <>
              <CardTitle className="flex items-center gap-2 border-transparent border-1 overflow-hidden">
                <span className="text-lg font-bold truncate">{project.name}</span>
                <span title={isLocked ? t`Simulation in progress - modifications disabled` : t`Edit name`} className={isLocked ? 'inline-flex cursor-not-allowed' : 'inline-flex'}>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={handleStartEditName}
                    disabled={isLocked}
                  >
                    <Pencil />
                  </Button>
                </span>
              </CardTitle>
              <CardAction>
                <span title={isLocked ? t`Simulation in progress - modifications disabled` : t`Delete project`} className={isLocked ? 'inline-flex cursor-not-allowed' : 'inline-flex'}>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="hover:text-red-600"
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={isLocked}
                  >
                    <Trash2 />
                  </Button>
                </span>
              </CardAction>
            </>
          )}
        </CardHeader>
        <CardContent>
          <div className="flex flex-col flex-wrap items-start gap-2 text-slate-500">
            {project.tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {project.tags.map((tag) => (
                  <span key={tag} className="badge-blue">
                    {tag}
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <User className="size-4" />
              <div>
                <Trans>
                  <span className="font-semibold">Created by:</span> {project.user.full_name}
                </Trans>
              </div>
            </div>
            <div className="flex items-center gap-2" title={absoluteTime(i18n, project.created_at)}>
              <CalendarPlus className="size-4" />
              <div>
                <Trans>
                  <span className="font-semibold">Created:</span> {relativeTime(i18n, project.created_at)}
                </Trans>
              </div>
            </div>
            <div className="flex items-center gap-2" title={absoluteTime(i18n, project.updated_at)}>
              <FilePen className="size-4" />
              <div>
                <Trans>
                  <span className="font-semibold">Updated:</span> {relativeTime(i18n, project.updated_at)}
                </Trans>
              </div>
            </div>
          </div>
        </CardContent>
        <CardFooter className={stateBannerClass(simulationState)}>
          <SimBannerContent
            projectId={project.id}
            state={simulationState}
            collisionCount={collisionCount}
            backgroundTaskId={backgroundTaskId}
            simulationErrorMessage={project.simulation_result?.error_message}
            onShowCancelConfirm={setShowCancelConfirm}
            cancelDetectionIsPending={cancelDetectionMutation.isPending}
          />
        </CardFooter>
      </Card>

      <ConfirmModal
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        onConfirm={handleDelete}
        title={t`Delete project?`}
        message={t`This action cannot be undone. The project and all its data will be permanently deleted.`}
        confirmLabel={t`Delete project`}
        confirmVariant="danger"
        isLoading={deleteProjectMutation.isPending}
      />
      <ConfirmModal
        open={showCancelConfirm}
        onOpenChange={setShowCancelConfirm}
        onConfirm={handleConfirmCancel}
        title={t`Cancel simulation?`}
        message={t`Are you sure you want to cancel the running collision detection? This action cannot be undone.`}
        confirmLabel={t`Yes, cancel`}
        cancelLabel={t`No`}
        confirmVariant="danger"
        isLoading={cancelDetectionMutation.isPending}
      />
    </>
  );
};
